/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { useState } from 'react'
import { DesiredSchedule, ReferralTimeFrame } from 'core/types'
import { Grid, Typography, Button } from '@mui/material'
import { CustomLabelWithToolTip } from 'Components/CustomLabel/Index'
import { DatePickerWithLabel } from 'Components/DatePicker'
import { toast } from 'react-toastify'
import { CurrentSchoolYear } from 'core/Utilities'

export interface ServiceTimeSelectionProps {
  isdID: number
  handleBack: () => void
  dataSelected: (timeOption: ReferralTimeFrame, schedule: DesiredSchedule, selectedDate: Date | null) => void
}

export function ServiceTimeSelection (props: ServiceTimeSelectionProps): JSX.Element {
  const [selectedTimeOption, setSelectedTimeOption] = useState<ReferralTimeFrame | null>(null)
  const [desiredSchedule, setDesiredSchedule] = useState<DesiredSchedule | null>(null)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)

  const currentSchoolYear = CurrentSchoolYear()

  function handleContinue (): void {
    if (desiredSchedule == null) {
      toast.error('Please select a desired schedule to continue')
    } else if (selectedTimeOption == null) {
      toast.error('Please select a service starting time to continue')
    } else if (selectedTimeOption === ReferralTimeFrame.SpecificDate && selectedDate == null) {
      toast.error('Please select a specific date to continue')
    } else {
      props.dataSelected(selectedTimeOption, desiredSchedule, selectedDate)
    }
  }

  return <Grid container spacing={2}>
    <Grid item xs={12}>
      <Typography variant='h5' sx={{ mb: '30px' }} component='div' data-testid="service-date-selection-prompt">
        <CustomLabelWithToolTip
          name='IF_Service_Date_Intro'
          isdId={props.isdID ?? 0}
          defaultLabelText='When are you looking to start this service?'
        />
      </Typography>
      <Typography variant='subtitle1' sx={{ mb: '30px' }} component='div'>
        <CustomLabelWithToolTip
          name='IF_Service_Date_Subtext'
          isdId={props.isdID ?? 0}
          defaultLabelText='If your children need multiple or different services, you will have the ability to add that later.'
        />
      </Typography>
    </Grid>

    <Grid item xs={12}>
      <Button
        variant="outlined"
        className={'wide-button' + (selectedTimeOption === ReferralTimeFrame.Immediate ? ' selected' : '')}
        data-testid='time-option-now'
        onClick={() => { setSelectedTimeOption(ReferralTimeFrame.Immediate) }}
      >
        Current School Year {currentSchoolYear}-{currentSchoolYear + 1}
      </Button>
    </Grid>
    {/* MIECC-2327: Upcoming School Year and Specific Date options temporarily removed at Oakland's request; restore in a later deployment */}

    {selectedTimeOption === ReferralTimeFrame.SpecificDate &&
      <Grid item xs={12}>
        <DatePickerWithLabel
          label='Specific Date'
          name='selectedDate'
          dataTestId='serviceDatePicker'
          value={selectedDate}
          onChange={(val) => { setSelectedDate(val) }}
          FormControlProps={{ className: 'pb-0 width-100' }}
        />
      </Grid>
    }

    <Grid item xs={12} sx={{ mt: '30px' }}>
      <Typography variant='h5' component='div'>
        <CustomLabelWithToolTip
          name='IF_Service_Time_Intro'
          isdId={props.isdID ?? 0}
          defaultLabelText='What time of day do you desire this service?'
        />
      </Typography>
    </Grid>

    <Grid item xs={12}>
      <Button
        variant="outlined"
        className={'wide-button' + (desiredSchedule === DesiredSchedule.Morning ? ' selected' : '')}
        data-testid='schedule-option-morning'
        onClick={() => { setDesiredSchedule(DesiredSchedule.Morning) }}
      >
        Morning
      </Button>
    </Grid>
    <Grid item xs={12}>
      <Button
        variant="outlined"
        className={'wide-button' + (desiredSchedule === DesiredSchedule.Afternoon ? ' selected' : '')}
        data-testid='schedule-option-afternoon'
        onClick={() => { setDesiredSchedule(DesiredSchedule.Afternoon) }}
      >
        Afternoon
      </Button>
    </Grid>
    <Grid item xs={12}>
      <Button
        variant="outlined"
        className={'wide-button' + (desiredSchedule === DesiredSchedule.Evening ? ' selected' : '')}
        data-testid='schedule-option-evening'
        onClick={() => { setDesiredSchedule(DesiredSchedule.Evening) }}
      >
        Evening
      </Button>
    </Grid>
    <Grid item xs={12}>
      <Button
        variant="outlined"
        className={'wide-button' + (desiredSchedule === DesiredSchedule.AllDay ? ' selected' : '')}
        data-testid='schedule-option-all-day'
        onClick={() => { setDesiredSchedule(DesiredSchedule.AllDay) }}
      >
        All Day
      </Button>
    </Grid>

    <Grid item xs={6} sx={{ mb: '20px', mt: '40px' }}>
      <Button
        name='backButton'
        className='back-button'
        data-testid='back-button'
        onClick={props.handleBack}
        variant='outlined'
      >
        Back
      </Button>
    </Grid>
    <Grid item xs={6} sx={{ mb: '20px', mt: '40px' }}>
      <Button
        name='continueButton'
        className='footer-button'
        data-testid='continue-button'
        onClick={handleContinue}
        variant='contained'
      >
        Continue
      </Button>
    </Grid>
  </Grid>
}
